/**
 * Frontend lazy loading functionality for Embed and Video blocks
 *
 * Implements intersection observer-based lazy loading with poster image support
 * for performance optimization of video content.
 */

let lazyVideoBlocks = [].slice.call(
	document.querySelectorAll( 'figure.wp-block-video' )
);
let lazyEmbedBlocks = [].slice.call(
	document.querySelectorAll( 'figure.wp-block-embed' )
);

/**
 * YouTube IFrame API initialization
 * Creates player instances for YouTube embeds when API is ready
 */
let youtubePlayer = null;
const youtubeIframeWrapper = document.querySelector(
	'.wp-block-embed-youtube div.wp-block-embed__wrapper'
);

if ( youtubeIframeWrapper ) {
	window.onYouTubeIframeAPIReady = () => {
		const iframe = youtubeIframeWrapper.getElementsByTagName( 'iframe' )[ 0 ];
		if ( iframe ) {
			youtubePlayer = new window.YT.Player( iframe, {
				events: {
					onReady: ( event ) => {
						// Player is ready
					},
				},
			} );
		}
	};
}

//replace src with data-src and unobserve videos
if ( lazyVideoBlocks.length > 0 && 'IntersectionObserver' in window ) {
	let lazyVideoObserver = new IntersectionObserver( function (
		entries,
		observer
	) {
		entries.forEach( function ( video ) {
			if ( video.isIntersecting ) {
				if (
					typeof video.target.tagName === 'string' &&
					video.target.tagName === 'VIDEO' &&
					video.target.dataset.src
				) {
					video.target.src = video.target.dataset.src;
				}

				video.target.load();
				lazyVideoObserver.unobserve( video.target );
			}
		} );
	} );

	//observe videos
	lazyVideoBlocks.forEach( function ( lazyVideoBlock ) {
		const poster = lazyVideoBlock.querySelector(
			'div.video-block__cover'
		);
		const video = lazyVideoBlock.getElementsByTagName( 'video' )[ 0 ];
		if ( poster !== null ) {
			//observe videos with poster after cover image click
			poster.addEventListener( 'click', function () {
				video.removeAttribute( 'style' );
				video.removeAttribute( 'poster' );
				poster.setAttribute( 'style', 'display: none;' );
				lazyVideoObserver.observe( video );
			} );
		} else {
			//observe videos without poster immediately when visible
			lazyVideoObserver.observe( video );
		}
	} );
}

/**
 * Handle lazy loading for embed blocks (YouTube and Vimeo)
 */
if ( lazyEmbedBlocks.length > 0 ) {
	lazyEmbedBlocks.forEach( function ( lazyEmbedBlock ) {
		const poster = lazyEmbedBlock.querySelector(
			'div.video-block__cover'
		);
		const iframeWrapper = lazyEmbedBlock.querySelector(
			'div.wp-block-embed__wrapper'
		);

		if ( ! iframeWrapper ) {
			return;
		}

		let embedType = null;
		if (
			iframeWrapper.parentNode.classList.contains(
				'wp-block-embed-vimeo'
			)
		) {
			embedType = 'vimeo';
		} else if (
			iframeWrapper.parentNode.classList.contains(
				'wp-block-embed-youtube'
			)
		) {
			embedType = 'youtube';
		}

		const iframe = iframeWrapper.getElementsByTagName( 'iframe' )[ 0 ];

		if ( ! iframe || ! iframe.dataset.src ) {
			return;
		}

		// Handle YouTube embeds
		if ( poster !== null && embedType === 'youtube' ) {
			poster.addEventListener( 'click', function () {
				const isModal = iframe.parentNode.classList.contains(
					'video-modal__embed'
				);

				// Set iframe src to trigger loading
				iframe.src = iframe.dataset.src;

				if ( isModal ) {
					// Show modal
					iframeWrapper.setAttribute( 'style', 'display: block;' );
					iframeWrapper.classList.add( 'video-modal__opened' );

					// Play video when YouTube API is ready
					if ( youtubePlayer && youtubePlayer.playVideo ) {
						youtubePlayer.playVideo();
					}
				} else {
					// Inline playback
					iframeWrapper.removeAttribute( 'style' );
					poster.setAttribute( 'style', 'display: none;' );
				}
			} );
		}

		// Handle Vimeo embeds
		if ( poster !== null && embedType === 'vimeo' ) {
			poster.addEventListener( 'click', function () {
				const isModal = iframe.parentNode.classList.contains(
					'video-modal__embed'
				);

				// Set iframe src to trigger loading
				iframe.src = iframe.dataset.src;

				if ( isModal ) {
					// Show modal
					iframeWrapper.setAttribute( 'style', 'display: block;' );
					iframeWrapper.classList.add( 'video-modal__opened' );

					// Initialize Vimeo Player and play
					if ( typeof window.Vimeo !== 'undefined' ) {
						iframe.addEventListener( 'load', function () {
							const player = new window.Vimeo.Player( iframe );
							player.play();
						} );
					}
				} else {
					// Inline playback
					iframeWrapper.removeAttribute( 'style' );
					poster.setAttribute( 'style', 'display: none;' );
				}
			} );
		}
	} );
}

/**
 * Modal close functionality
 * Closes video modals when clicking outside the video content
 */
const videoModals = document.querySelectorAll( '.video-modal__opened' );

videoModals.forEach( function ( modal ) {
	modal.addEventListener( 'click', function ( event ) {
		// Only close if clicking the modal wrapper, not the iframe
		if ( event.target === modal ) {
			closeVideoModal( modal );
		}
	} );
} );

/**
 * Close video modal and stop playback
 *
 * @param {HTMLElement} modal The modal wrapper element
 */
function closeVideoModal( modal ) {
	const iframe = modal.querySelector( 'iframe' );

	if ( ! iframe ) {
		return;
	}

	// Stop Vimeo video
	if ( typeof window.Vimeo !== 'undefined' ) {
		const player = new window.Vimeo.Player( iframe );
		player.pause().catch( function () {
			// Player might not be ready yet
		} );
	}

	// Stop YouTube video
	if ( youtubePlayer && youtubePlayer.pauseVideo ) {
		youtubePlayer.pauseVideo();
	}

	// Hide modal
	modal.setAttribute( 'style', 'display: none;' );
	modal.classList.remove( 'video-modal__opened' );
}

/**
 * Add ESC key handler to close modals
 */
document.addEventListener( 'keydown', function ( event ) {
	if ( event.key === 'Escape' || event.keyCode === 27 ) {
		const openModals = document.querySelectorAll( '.video-modal__opened' );
		openModals.forEach( function ( modal ) {
			closeVideoModal( modal );
		} );
	}
} );
